import type { EditorCommand, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { BlockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import { NodeSelection, TextSelection } from '@atlaskit/editor-prosemirror/state';
import { CellSelection } from '@atlaskit/editor-tables';

import { malleableUiPluginKey } from '../pm-plugins/plugin-key';
import type { RemixThinkingPluginMeta } from '../pm-plugins/remix-thinking-plugin';
import { remixThinkingPluginKey } from '../pm-plugins/remix-thinking-plugin';

export const makeStartRemixThinking =
	(api: ExtractInjectionAPI<BlockControlsPlugin> | undefined) =>
	(): EditorCommand =>
	({ tr }) => {
		const selection = tr.selection;
		const isNodeSelection = selection instanceof NodeSelection;
		const isCellSelection = selection instanceof CellSelection;

		const thinkingMeta: RemixThinkingPluginMeta = {
			isThinking: true,
			thinkingSelectionFrom: selection.from,
			thinkingSelectionTo: isNodeSelection
				? selection.from + selection.node.nodeSize
				: selection.to,
			isNodeSelection: isNodeSelection,
		};

		if (isCellSelection) {
			thinkingMeta.cellSelectionRanges = selection.ranges.map((range) => ({
				from: range.$from.pos,
				to: range.$to.pos,
			}));
			thinkingMeta.anchorCellPos = selection.$anchorCell.pos;
			thinkingMeta.headCellPos = selection.$headCell.pos;
		}

		// Clear block selection decorations before applying the thinking state.
		api?.selection?.commands?.clearBlockSelection()({ tr });

		tr.setMeta(remixThinkingPluginKey, thinkingMeta);
		tr.setMeta(malleableUiPluginKey, { isRemixModalOpen: false });

		// Collapse the cursor to the end of the selection so the user can
		// continue editing while the remix is processing.
		const endPos = isNodeSelection ? selection.from + selection.node.nodeSize : selection.to;
		tr.setSelection(TextSelection.create(tr.doc, endPos));

		return tr;
	};
